/**
 * Company Resources query and mutation helpers using Gen 2 typed client.
 */
import { authClient, client } from "../utils/amplifyClient";
import { costCategoryOwnedByCompany } from "../utils/costTrackingUtils";
import { getGraphQlErrorMessage } from "../utils/graphqlErrorUtils";
import { updateDocument as updateLegacyDocumentMutation, updateVideo as updateLegacyVideoMutation } from "./mutations";
import type { ServiceProcedure, ServiceProcedureConsumable } from "../types/companyResourceTypes";
import type { ServiceCenter } from "../types/schema";

/** Fields that exist on the Gen 2 PMPartsAndProcedure model. Extra keys (e.g. editHistory) are rejected by AppSync. */
const SERVICE_ITEM_INPUT_KEYS = [
    'id',
    'pmConfigurationId',
    'companyId',
    'itemName',
    'quantity',
    'manufacturer',
    'partNumber',
    'vendorId',
    'vendorName',
    'price',
    'sortOrder',
    'category',
    'itemType',
    'unitOfMeasure',
    'pmConfigurationIds',
    'expenseCategoryId',
    'expenseCategoryName',
    'createdByUserId',
    'createdByUserName',
] as const;

type CreateServiceItemInput = Parameters<typeof authClient.models.PMPartsAndProcedure.create>[0];
type UpdateServiceItemInput = Parameters<typeof authClient.models.PMPartsAndProcedure.update>[0];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toServiceItemInput(input: Record<string, any>): CreateServiceItemInput {
    const next: Record<string, unknown> = {};
    for (const key of SERVICE_ITEM_INPUT_KEYS) {
        if (input[key] !== undefined) next[key] = input[key];
    }
    // Callers pass a full record; the whitelist above drops AppSync-rejected keys.
    return next as CreateServiceItemInput;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function throwIfGraphQlErrors(result: { errors?: unknown }, fallback: string) {
    const errors = result.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        throw new Error(getGraphQlErrorMessage(result) || fallback);
    }
}

// ---------------------------------------------------------------------------
// CompanyResource
// ---------------------------------------------------------------------------

export async function listCompanyResourcesByCompanyId(companyId: string) {
    const { data } = await authClient.models.CompanyResource.listCompanyResourceByCompanyId({ companyId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createCompanyResource(input: any) {
    const { data } = await authClient.models.CompanyResource.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateCompanyResource(input: any) {
    const { data } = await authClient.models.CompanyResource.update(input);
    return data;
}

export async function deleteCompanyResource(id: string) {
    const { data } = await authClient.models.CompanyResource.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Service Items (PMPartsAndProcedure with companyId index)
// ---------------------------------------------------------------------------

export async function listServiceItemsByCompanyId(companyId: string) {
    const allItems: NonNullable<(typeof firstPage)[number]>[] = [];
    let nextToken: string | null | undefined;

    const { data: firstPage, nextToken: token } =
        await authClient.models.PMPartsAndProcedure.listPMPartsAndProcedureByCompanyId(
            { companyId },
            { limit: 1000, nextToken: undefined },
        );
    for (const item of firstPage ?? []) { if (item) allItems.push(item); }
    nextToken = token;

    while (nextToken) {
        const { data: page, nextToken: next } =
            await authClient.models.PMPartsAndProcedure.listPMPartsAndProcedureByCompanyId(
                { companyId },
                { limit: 1000, nextToken },
            );
        for (const item of page ?? []) { if (item) allItems.push(item); }
        nextToken = next;
    }

    return allItems;
}

/**
 * Create and update both pass through here. An expense category id is stored
 * only when that category's company matches the service item. A blank id
 * clears the link. The name is copied from the category record.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function withOwnedCostCategory(input: Record<string, any>) {
    const touchesCategory = input.expenseCategoryId !== undefined || input.expenseCategoryName !== undefined;
    if (!touchesCategory) return input;

    const rawId = typeof input.expenseCategoryId === 'string' ? input.expenseCategoryId.trim() : '';
    if (!rawId) {
        return { ...input, expenseCategoryId: null, expenseCategoryName: null };
    }

    let companyId = typeof input.companyId === 'string' ? input.companyId.trim() : '';
    if (!companyId && typeof input.id === 'string' && input.id) {
        const existing = await authClient.models.PMPartsAndProcedure.get({ id: input.id });
        throwIfGraphQlErrors(existing, 'Failed to load service item');
        companyId = existing.data?.companyId?.trim() || '';
    }

    const category = await authClient.models.ExpenseCategory.get({ id: rawId });
    throwIfGraphQlErrors(category, 'Failed to load cost category');
    const owned = costCategoryOwnedByCompany(companyId, category.data);
    return { ...input, ...owned };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createServiceItem(input: any) {
    const result = await authClient.models.PMPartsAndProcedure.create(
        toServiceItemInput(await withOwnedCostCategory(input)),
    );
    throwIfGraphQlErrors(result, 'Failed to create service item');
    return result.data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateServiceItem(input: any) {
    const result = await authClient.models.PMPartsAndProcedure.update(
        toServiceItemInput(await withOwnedCostCategory(input)) as UpdateServiceItemInput,
    );
    throwIfGraphQlErrors(result, 'Failed to update service item');
    return result.data;
}

export async function deleteServiceItem(id: string) {
    const { data } = await authClient.models.PMPartsAndProcedure.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Service Procedures (company catalog of service steps; not Service Items)
// ---------------------------------------------------------------------------

const SERVICE_PROCEDURE_INPUT_KEYS = [
    'id',
    'companyId',
    'category',
    'itemName',
    'categorySortOrder',
    'createdByUserId',
    'createdByUserName',
] as const;

type ServiceProcedureInput = {
    id?: string;
    companyId?: string;
    category?: string;
    itemName?: string;
    categorySortOrder?: number | null;
    createdByUserId?: string | null;
    createdByUserName?: string | null;
};

function toServiceProcedureRecord(item: {
    id: string;
    companyId: string;
    category: string;
    itemName: string;
    categorySortOrder?: number | null;
    createdByUserId?: string | null;
    createdByUserName?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}): ServiceProcedure {
    return {
        id: item.id,
        companyId: item.companyId,
        category: item.category,
        itemName: item.itemName,
        categorySortOrder: item.categorySortOrder ?? null,
        createdByUserId: item.createdByUserId ?? null,
        createdByUserName: item.createdByUserName ?? null,
        createdAt: item.createdAt ?? null,
        updatedAt: item.updatedAt ?? null,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toServiceProcedureInput(input: Record<string, any>): ServiceProcedureInput {
    const next: ServiceProcedureInput = {};
    for (const key of SERVICE_PROCEDURE_INPUT_KEYS) {
        if (input[key] !== undefined) next[key] = input[key];
    }
    return next;
}

export async function listServiceProceduresByCompanyId(companyId: string): Promise<ServiceProcedure[]> {
    const tenantId = requireCompanyId(companyId);
    const allItems: ServiceProcedure[] = [];
    const listByCompany = authClient.models.ServiceProcedure.listServiceProcedureByCompanyId as (
        input: { companyId: string; nextToken?: string | null },
    ) => ReturnType<typeof authClient.models.ServiceProcedure.listServiceProcedureByCompanyId>;

    let nextToken: string | null | undefined;
    const first = await listByCompany({ companyId: tenantId });
    throwIfGraphQlErrors(first, 'Failed to load service procedures');
    for (const item of first.data ?? []) {
        if (item) allItems.push(toServiceProcedureRecord(item));
    }
    nextToken = first.nextToken;

    while (nextToken) {
        const page = await listByCompany({ companyId: tenantId, nextToken });
        throwIfGraphQlErrors(page, 'Failed to load service procedures');
        for (const item of page.data ?? []) {
            if (item) allItems.push(toServiceProcedureRecord(item));
        }
        nextToken = page.nextToken;
    }

    return allItems;
}

function requireCompanyId(companyId: string | undefined | null): string {
    const id = companyId?.trim();
    if (!id) throw new Error('Company is required');
    return id;
}

async function getServiceProcedureForCompany(id: string, companyId: string): Promise<ServiceProcedure> {
    const result = await authClient.models.ServiceProcedure.get({ id });
    throwIfGraphQlErrors(result, 'Failed to load service procedure');
    if (!result.data || result.data.companyId !== companyId) {
        throw new Error('Service procedure not found');
    }
    return toServiceProcedureRecord(result.data);
}

async function getServiceItemForCompany(id: string, companyId: string): Promise<void> {
    const result = await authClient.models.PMPartsAndProcedure.get({ id });
    throwIfGraphQlErrors(result, 'Failed to load service item');
    if (!result.data || result.data.companyId !== companyId) {
        throw new Error('Service item not found');
    }
}

async function getConsumableLinkForCompany(
    id: string,
    companyId: string,
): Promise<ServiceProcedureConsumable> {
    const result = await authClient.models.ServiceProcedureConsumable.get({ id });
    throwIfGraphQlErrors(result, 'Failed to load procedure consumable');
    if (!result.data || result.data.companyId !== companyId) {
        throw new Error('Procedure consumable not found');
    }
    return toConsumableLinkRecord(result.data);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createServiceProcedure(input: any): Promise<ServiceProcedure> {
    const companyId = requireCompanyId(input?.companyId);
    const result = await authClient.models.ServiceProcedure.create(
        toServiceProcedureInput({ ...input, companyId }) as { companyId: string; category: string; itemName: string },
    );
    throwIfGraphQlErrors(result, 'Failed to create service procedure');
    if (!result.data) {
        throw new Error('Failed to create service procedure');
    }
    return toServiceProcedureRecord(result.data);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateServiceProcedure(input: any): Promise<ServiceProcedure> {
    const companyId = requireCompanyId(input?.companyId);
    if (!input?.id) throw new Error('Service procedure is required');
    await getServiceProcedureForCompany(input.id, companyId);
    const result = await authClient.models.ServiceProcedure.update(
        toServiceProcedureInput({ ...input, companyId }) as { id: string },
    );
    throwIfGraphQlErrors(result, 'Failed to update service procedure');
    if (!result.data) {
        throw new Error('Failed to update service procedure');
    }
    return toServiceProcedureRecord(result.data);
}

export async function deleteServiceProcedure(id: string, companyId: string) {
    await getServiceProcedureForCompany(id, requireCompanyId(companyId));
    const { data } = await authClient.models.ServiceProcedure.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Service Procedure Consumables (procedure → Service Item links)
// ---------------------------------------------------------------------------

function toConsumableLinkRecord(item: {
    id: string;
    companyId: string;
    procedureId: string;
    serviceItemId: string;
    quantity?: number | null;
    sortOrder?: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}): ServiceProcedureConsumable {
    return {
        id: item.id,
        companyId: item.companyId,
        procedureId: item.procedureId,
        serviceItemId: item.serviceItemId,
        quantity: item.quantity ?? null,
        sortOrder: item.sortOrder ?? null,
        createdAt: item.createdAt ?? null,
        updatedAt: item.updatedAt ?? null,
    };
}

export async function listServiceProcedureConsumablesByCompanyId(
    companyId: string,
): Promise<ServiceProcedureConsumable[]> {
    const tenantId = requireCompanyId(companyId);
    const model = authClient.models.ServiceProcedureConsumable;
    const listByCompany = model.listServiceProcedureConsumableByCompanyId as (
        input: { companyId: string; nextToken?: string | null },
    ) => ReturnType<typeof model.listServiceProcedureConsumableByCompanyId>;

    const collectFromGsi = async () => {
        const allItems: ServiceProcedureConsumable[] = [];
        let nextToken: string | null | undefined;
        const first = await listByCompany({ companyId: tenantId });
        throwIfGraphQlErrors(first, 'Failed to load procedure consumables');
        for (const item of first.data ?? []) {
            if (item) allItems.push(toConsumableLinkRecord(item));
        }
        nextToken = first.nextToken;
        while (nextToken) {
            const page = await listByCompany({ companyId: tenantId, nextToken });
            throwIfGraphQlErrors(page, 'Failed to load procedure consumables');
            for (const item of page.data ?? []) {
                if (item) allItems.push(toConsumableLinkRecord(item));
            }
            nextToken = page.nextToken;
        }
        return allItems;
    };

    try {
        if (typeof listByCompany !== 'function') {
            throw new Error('Procedure consumable index is unavailable');
        }
        return await collectFromGsi();
    } catch (err) {
        console.warn('Falling back to filtered list for procedure consumables:', err);
        const allItems: ServiceProcedureConsumable[] = [];
        const first = await model.list({
            filter: { companyId: { eq: tenantId } },
        });
        throwIfGraphQlErrors(first, 'Failed to load procedure consumables');
        for (const item of first.data ?? []) {
            if (item) allItems.push(toConsumableLinkRecord(item));
        }
        let nextToken = first.nextToken;
        while (nextToken) {
            const page = await model.list({
                filter: { companyId: { eq: tenantId } },
                nextToken,
            });
            throwIfGraphQlErrors(page, 'Failed to load procedure consumables');
            for (const item of page.data ?? []) {
                if (item) allItems.push(toConsumableLinkRecord(item));
            }
            nextToken = page.nextToken;
        }
        return allItems;
    }
}

export async function listServiceProcedureConsumablesByProcedureId(
    procedureId: string,
    companyId: string,
): Promise<ServiceProcedureConsumable[]> {
    const tenantId = requireCompanyId(companyId);
    const allItems: ServiceProcedureConsumable[] = [];
    const listByProcedure = authClient.models.ServiceProcedureConsumable
        .listServiceProcedureConsumableByProcedureId as (
        input: { procedureId: string; nextToken?: string | null },
    ) => ReturnType<typeof authClient.models.ServiceProcedureConsumable.listServiceProcedureConsumableByProcedureId>;

    let nextToken: string | null | undefined;
    const first = await listByProcedure({ procedureId });
    throwIfGraphQlErrors(first, 'Failed to load procedure consumables');
    for (const item of first.data ?? []) {
        if (item && item.companyId === tenantId) allItems.push(toConsumableLinkRecord(item));
    }
    nextToken = first.nextToken;
    while (nextToken) {
        const page = await listByProcedure({ procedureId, nextToken });
        throwIfGraphQlErrors(page, 'Failed to load procedure consumables');
        for (const item of page.data ?? []) {
            if (item && item.companyId === tenantId) allItems.push(toConsumableLinkRecord(item));
        }
        nextToken = page.nextToken;
    }
    return allItems;
}

export async function createServiceProcedureConsumable(input: {
    companyId: string;
    procedureId: string;
    serviceItemId: string;
    quantity?: number | null;
    sortOrder?: number;
}): Promise<ServiceProcedureConsumable> {
    const companyId = requireCompanyId(input.companyId);
    await getServiceProcedureForCompany(input.procedureId, companyId);
    await getServiceItemForCompany(input.serviceItemId, companyId);
    const result = await authClient.models.ServiceProcedureConsumable.create({
        ...input,
        companyId,
    });
    throwIfGraphQlErrors(result, 'Failed to link consumable');
    if (!result.data) {
        throw new Error('Failed to link consumable');
    }
    return toConsumableLinkRecord(result.data);
}

export async function updateServiceProcedureConsumable(input: {
    id: string;
    companyId: string;
    quantity?: number | null;
    sortOrder?: number | null;
}): Promise<ServiceProcedureConsumable> {
    const companyId = requireCompanyId(input.companyId);
    if (!input.id) throw new Error('Procedure consumable is required');
    await getConsumableLinkForCompany(input.id, companyId);
    const result = await authClient.models.ServiceProcedureConsumable.update({
        id: input.id,
        ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
        ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
    });
    throwIfGraphQlErrors(result, 'Failed to update consumable');
    if (!result.data) {
        throw new Error('Failed to update consumable');
    }
    return toConsumableLinkRecord(result.data);
}

export async function deleteServiceProcedureConsumable(id: string, companyId: string) {
    await getConsumableLinkForCompany(id, requireCompanyId(companyId));
    const { data } = await authClient.models.ServiceProcedureConsumable.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export async function listVendorsByCompanyId(companyId: string) {
    const { data } = await authClient.models.VendorList.listVendorListByCompanyIdAndUpdatedAt({ companyId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createVendor(input: any) {
    const { data } = await authClient.models.VendorList.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateVendor(input: any) {
    const { data } = await authClient.models.VendorList.update(input);
    return data;
}

// ---------------------------------------------------------------------------
// Service Centers
// ---------------------------------------------------------------------------

export type ServiceCenterWriteInput = {
    companyId: string;
    name: string;
    address?: string | null;
    phoneNumber?: string | null;
    faxNumber?: string | null;
    repName?: string | null;
    repPhoneNumber?: string | null;
    regionIds?: string[];
    locationIds?: string[];
};

function isServiceCenter(item: ServiceCenter | null | undefined): item is ServiceCenter {
    return Boolean(item?.id);
}

export async function listServiceCentersByCompanyId(companyId: string): Promise<ServiceCenter[]> {
    const result = await authClient.models.ServiceCenter.listServiceCenterByCompanyIdAndUpdatedAt({ companyId });
    throwIfGraphQlErrors(result, 'Failed to load service centers');
    return (result.data ?? []).filter(isServiceCenter);
}

export async function createServiceCenter(input: ServiceCenterWriteInput) {
    const result = await authClient.models.ServiceCenter.create({
        ...input,
        updatedAt: new Date().toISOString(),
    });
    throwIfGraphQlErrors(result, 'Failed to create service center');
    return result.data;
}

export async function updateServiceCenter(input: ServiceCenterWriteInput & { id: string; archived?: boolean | null }) {
    const result = await authClient.models.ServiceCenter.update({
        ...input,
        updatedAt: new Date().toISOString(),
    });
    throwIfGraphQlErrors(result, 'Failed to update service center');
    return result.data;
}

// ---------------------------------------------------------------------------
// Labor Categories & Rates
// ---------------------------------------------------------------------------

export async function listLaborCategoriesByCompanyId(companyId: string) {
    const { data } = await authClient.models.LaborCategory.listLaborCategoryByCompanyId({ companyId });
    return data;
}

export async function listLaborRatesByCategoryId(categoryId: string) {
    const { data } = await authClient.models.LaborRate.listLaborRateByCategoryIdAndEffectiveDate({ categoryId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createLaborCategory(input: any) {
    const { data } = await authClient.models.LaborCategory.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLaborCategory(input: any) {
    const { data } = await authClient.models.LaborCategory.update(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createLaborRate(input: any) {
    const { data } = await authClient.models.LaborRate.create(input);
    return data;
}

// ---------------------------------------------------------------------------
// Training Schedule helpers (used by AssignFormsModal)
// ---------------------------------------------------------------------------

export async function listTrainingSchedulesByTopicId(topicId: string) {
    // TrainingSchedule doesn't have a topicId index, filter client-side
    const { data } = await authClient.models.TrainingSchedule.list();
    return data.filter(s => s.topicId === topicId);
}

export async function deleteTrainingScheduleEntry(id: string) {
    const { data } = await authClient.models.TrainingSchedule.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Legacy Documents & Videos
// ---------------------------------------------------------------------------


export async function listLegacyDocumentsByCompany(_companyId: string) {
    // Documents don't have a companyId index, fetch all and filter
    const { data } = await authClient.models.Document.list();
    return data;
}

export async function listLegacyVideosByCompany(_companyId: string) {
    const { data } = await authClient.models.Video.list();
    return data;
}

export async function deleteLegacyDocument(id: string) {
    const { data } = await authClient.models.Document.delete({ id });
    return data;
}

export async function deleteLegacyVideo(id: string) {
    const { data } = await authClient.models.Video.delete({ id });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLegacyDocumentFolder(id: string, folderName: string): Promise<any> {
  const result = await client.graphql({
    query: updateLegacyDocumentMutation,
    variables: { input: { id, folderName } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateDocument;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLegacyVideoFolder(id: string, folderName: string): Promise<any> {
  const result = await client.graphql({
    query: updateLegacyVideoMutation,
    variables: { input: { id, folderName } },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result as any).data.updateVideo;
}

// ---------------------------------------------------------------------------
// Form Templates
// ---------------------------------------------------------------------------

export async function listFormTemplateGroupsByCompany(_companyId: string) {
    // FormTemplateGroup doesn't have companyId index, fetch all
    const { data } = await authClient.models.FormTemplateGroup.list();
    return data;
}

export async function listFormTemplatesByGroup(groupId: string) {
    // FormTemplate doesn't have a groupId index, filter client-side
    const { data } = await authClient.models.FormTemplate.list();
    return data.filter(t => t.formTemplateGroupID === groupId);
}

export async function removeCompanyFromTemplateGroup(groupId: string, companyId: string, currentSharedIds: string[]) {
    const updatedIds = currentSharedIds.filter(id => id !== companyId);
    const { data } = await authClient.models.FormTemplateGroup.update({
        id: groupId,
        sharedWithCompanyIds: updatedIds,
    });
    return data;
}

export async function listFormsByCompany(companyId: string) {
    const { data } = await authClient.models.Form.listFormByCompanyId({ companyId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createFormTemplate(input: any) {
    const { data } = await authClient.models.FormTemplate.create(input);
    return data;
}

export async function deleteFormTemplate(id: string) {
    const { data } = await authClient.models.FormTemplate.delete({ id });
    return data;
}
